/* Author-facing tax profile card — surfaces on the Business Dashboard.
 * Lets each author submit their own W-9/W-8BEN + PAN so payouts and
 * 1099s route without a manual admin chase.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Landmark, Save, Loader2 } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

const FORMS = ['W-9', 'W-8BEN', 'W-8BEN-E'];

export default function MyTaxProfileCard() {
  const [profile, setProfile] = useState(null);
  const [draft, setDraft] = useState({
    country: 'US', state: '', tax_form: 'W-9',
    ein_or_ssn: '', foreign_tin: '', per_country_tins: {},
  });
  const [busy, setBusy] = useState(false);
  const [showKeys, setShowKeys] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const r = await http.get('/authors/me/tax-profile');
      setProfile(r.data.profile);
      setDraft({
        country: r.data.profile.country || 'US',
        state: r.data.profile.state || '',
        tax_form: r.data.profile.tax_form || 'W-9',
        ein_or_ssn: '',
        foreign_tin: '',
        per_country_tins: r.data.profile.per_country_tins || {},
      });
    } catch (_e) { /* silent */ }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...draft };
      if (!payload.ein_or_ssn) delete payload.ein_or_ssn;
      if (!payload.foreign_tin) delete payload.foreign_tin;
      const r = await http.put('/authors/me/tax-profile', payload);
      setProfile(r.data.profile);
      toast.success('Tax profile saved. Payouts will route with this info.');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Save failed.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid="my-tax-profile-card">
      <div className="mb-2 flex items-center gap-2 text-[13px] font-semibold tracking-tight">
        <Landmark className="h-4 w-4 text-primary" /> My Tax Profile
      </div>
      <p className="mb-3 text-[11.5px] text-muted-foreground">
        US authors submit Form W-9; non-US authors submit Form W-8BEN.
        Add your country's TIN (e.g. India PAN, Taiwan VAT) to avoid withholding.
      </p>
      {profile?.updated_at && (
        <div className="mb-2 text-[10.5px] text-muted-foreground">
          Last saved {new Date(profile.updated_at).toLocaleString()}
          {profile.ein_or_ssn_masked && <> · SSN/EIN on file: <strong>{profile.ein_or_ssn_masked}</strong></>}
          {profile.foreign_tin_masked && <> · Foreign TIN on file: <strong>{profile.foreign_tin_masked}</strong></>}
        </div>
      )}
      <div className="grid gap-2 md:grid-cols-2">
        <label>
          <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Country</div>
          <input
            className="mt-1 h-7 w-full rounded-sm border border-border bg-background px-2 text-[12px]"
            value={draft.country} maxLength={4}
            data-testid="my-tax-country"
            onChange={(e) => setDraft({ ...draft, country: e.target.value.toUpperCase() })}
          />
        </label>
        <label>
          <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">State (if US)</div>
          <input
            className="mt-1 h-7 w-full rounded-sm border border-border bg-background px-2 text-[12px]"
            value={draft.state} maxLength={2}
            data-testid="my-tax-state"
            onChange={(e) => setDraft({ ...draft, state: e.target.value.toUpperCase() })}
          />
        </label>
        <label>
          <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Tax form</div>
          <select
            className="mt-1 h-7 w-full rounded-sm border border-border bg-background px-2 text-[12px]"
            value={draft.tax_form}
            data-testid="my-tax-form"
            onChange={(e) => setDraft({ ...draft, tax_form: e.target.value })}
          >
            {FORMS.map((f) => <option key={f}>{f}</option>)}
          </select>
        </label>
        <label>
          <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">EIN / SSN / ITIN</div>
          <input
            className="mt-1 h-7 w-full rounded-sm border border-border bg-background px-2 text-[12px]"
            placeholder={profile?.ein_or_ssn_masked || '—'}
            type={showKeys ? 'text' : 'password'}
            value={draft.ein_or_ssn}
            data-testid="my-tax-ein"
            onChange={(e) => setDraft({ ...draft, ein_or_ssn: e.target.value })}
          />
        </label>
        <label>
          <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Foreign TIN (if non-US)</div>
          <input
            className="mt-1 h-7 w-full rounded-sm border border-border bg-background px-2 text-[12px]"
            placeholder={profile?.foreign_tin_masked || '—'}
            type={showKeys ? 'text' : 'password'}
            value={draft.foreign_tin}
            data-testid="my-tax-foreign-tin"
            onChange={(e) => setDraft({ ...draft, foreign_tin: e.target.value })}
          />
        </label>
      </div>
      <label className="mt-2 flex items-center gap-1.5 text-[10.5px] text-muted-foreground">
        <input type="checkbox" checked={showKeys} onChange={(e) => setShowKeys(e.target.checked)}
               data-testid="my-tax-toggle-visibility" />
        Show unmasked IDs while editing
      </label>
      <div className="mt-3">
        <button
          type="button" onClick={save} disabled={busy}
          data-testid="my-tax-save"
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-[12.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save my tax profile
        </button>
      </div>
    </div>
  );
}
